
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import Logo from '@/components/Logo';
import { requestPasswordReset } from '@/contexts/auth/services/passwordService';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await requestPasswordReset(email);

      if (result.success) {
        setIsEmailSent(true);
        toast({
          title: "Email elküldve",
          description: "A jelszó visszaállítási útmutatót elküldtük az email címére.",
        });
      } else {
        toast({
          title: "Hiba történt",
          description: result.message || "Nem sikerült elküldeni a jelszó visszaállítási emailt. Kérjük, próbálja újra.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast({
        title: "Hiba történt",
        description: "Nem sikerült elküldeni a jelszó visszaállítási emailt. Kérjük, próbálja újra.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-6">
            <Logo className="h-12" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {isEmailSent ? "Email elküldve" : "Jelszó visszaállítása"}
          </CardTitle>
          <CardDescription className="text-center">
            {isEmailSent
              ? "Kérjük, ellenőrizze email fiókját a folytatáshoz"
              : "Adja meg email címét a jelszó visszaállításához"}
          </CardDescription>
        </CardHeader>

        {!isEmailSent ? (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email cím</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Feldolgozás...
                  </>
                ) : (
                  "Visszaállítási email küldése"
                )}
              </Button>

              <div className="text-center text-sm">
                <Link
                  to="/login"
                  className="inline-flex items-center text-medihub-primary hover:underline"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Vissza a bejelentkezéshez
                </Link>
              </div>
            </CardFooter>
          </form>
        ) : (
          <CardContent className="space-y-6">
            <div className="flex justify-center my-6">
              <Mail className="h-16 w-16 text-medihub-primary" />
            </div>

            <div className="text-center space-y-2">
              <p>
                Ha az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító linket erre a címre: <strong>{email}</strong>
              </p>
              <p>
                Kérjük, kattintson az emailben található linkre a jelszó visszaállításához.
              </p>
            </div>

            <div className="border rounded-md p-4 bg-muted/50">
              <h4 className="font-medium mb-2">Nem kapta meg az emailt?</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Ellenőrizze a spam vagy levélszemét mappát. Ha továbbra sem találja:
              </p>
              <Button
                variant="outline"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Küldés folyamatban...
                  </>
                ) : (
                  "Email újraküldése"
                )}
              </Button>
            </div>

            <div className="text-center text-sm pt-4">
              <Link
                to="/login"
                className="text-medihub-primary hover:underline"
              >
                Vissza a bejelentkezéshez
              </Link>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;
